/**
 * Ask Argus — Enterprise AI Chat Interface
 *
 * R2 demo port — layout adopts the founder-blessed demo design:
 *   <div className="flex h-full overflow-hidden">
 *     <Left  w-[280px] shrink-0 />                       (ALWAYS visible)
 *     <Center flex-1 min-w-0 />                          (chat thread)
 *     <Right transition w-[300px]|w-0 />                 (context panel)
 *   </div>
 *
 * The ResizablePanelGroup / ChatShell wrapper is removed entirely.
 * Each side panel is a fixed-width column; the right panel collapses
 * to zero via a width transition, NOT via `collapsible`. This kills
 * the "snap to 50px" bug that plagued the Wave 2A/3C build.
 *
 * Wiring is REAL — every component below talks to the live backend:
 *   - GET    /api/v1/conversations                 (sidebar list via useInfiniteQuery)
 *   - POST   /api/v1/conversations                 (create on first send)
 *   - GET    /api/v1/conversations/:id/messages    (history)
 *   - POST   /api/v1/ecosystem/query               (SSE streaming chat via useChatStream)
 *   - PATCH/DELETE/POST /api/v1/conversations/:id  (modal actions)
 *   - GET    /api/v1/conversations/:id/attached-tools (chat-tools-strip)
 *   - POST   /api/v1/conversations/:id/messages/:msgId/feedback (rating)
 *
 * No mock data imported anywhere.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { PanelRightClose, PanelRight, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ConversationSidebar,
  ChatMessages,
  ChatInput,
  ContextPanel,
  AskArgusWelcome,
  ChatToolsStrip,
  AttachedHighlightsChips,
  ClassificationBanner,
} from '@/components/ask-argus';
import { chatStreamPhaseToMessagesPhase } from '@/components/ask-argus/chat-messages';
import { ShareLinkDialog } from '@/components/ask-argus/share-link-dialog';
import { OfflineBanner } from '@/components/ask-argus/offline-banner';
import { ReconnectBadge } from '@/components/ask-argus/reconnect-badge';
import { ChatAnnouncerProvider } from '@/components/ask-argus/chat-announcer';
import type { ChatSubmitContext as ComposerChatSubmitContext } from '@/components/ask-argus/chat-input';
import {
  DEFAULT_TOGGLES,
  serializeTogglesForRequest,
} from '@/components/ask-argus/chat-input.helpers';
import type { Message, Citation, PipelineStage } from '@/lib/chat-types';
import { useChatStream } from '@/lib/chat/use-chat-stream';
import { bannerFromMetadata } from '@/lib/chat/message-hydration';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  useAttachedHighlights,
  formatHighlightsForSubmit,
} from '@/contexts/attached-highlights-context';
import { CitationVisibilityProvider } from '@/contexts/citation-visibility-context';
import { cn } from '@/lib/utils';

// UUID predicate so we don't hit /conversations/:id/messages on a
// transient client-only id.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ── Wire shapes ─────────────────────────────────────────────────────────────

interface MessageDTO {
  id: string;
  conversationId: string;
  role: string;
  content: string;
  citations: unknown;
  toolCalls: unknown;
  metadata: unknown;
  feedback: unknown;
  createdAt: string;
  agentId?: string | null;
}

interface ConversationDTO {
  id: string;
  title: string;
  status: string;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function dtoToMessage(m: MessageDTO): Message {
  const meta = (m.metadata as Record<string, unknown> | null) ?? {};
  const citations = Array.isArray(m.citations)
    ? (m.citations as Citation[])
    : undefined;
  const toolCalls = Array.isArray(m.toolCalls)
    ? (m.toolCalls as Message['toolCalls'])
    : undefined;
  const agentId =
    typeof (m as { agentId?: unknown }).agentId === 'string'
      ? ((m as { agentId: string }).agentId)
      : typeof meta.agentId === 'string'
        ? (meta.agentId as string)
        : undefined;
  const feedbackRaw = (m.feedback as Record<string, unknown> | null) ?? null;
  const feedback =
    feedbackRaw &&
    (feedbackRaw.rating === 'positive' || feedbackRaw.rating === 'negative')
      ? {
          rating: feedbackRaw.rating as 'positive' | 'negative',
          ...(typeof feedbackRaw.reason === 'string'
            ? { reason: feedbackRaw.reason }
            : typeof feedbackRaw.comment === 'string'
              ? { reason: feedbackRaw.comment }
              : {}),
        }
      : undefined;
  return {
    id: m.id,
    role: m.role === 'assistant' ? 'assistant' : 'user',
    content: m.content,
    agentId,
    citations,
    toolCalls,
    feedback,
    // bug — confidence scale mismatch. The LIVE `confidence` SSE event sends a
    // 0..1 value, but the PERSISTED message metadata stores 0..100 (the gate's
    // confidenceScore.total). The chip (deriveConfidenceChip) and trace drawer
    // both assume 0..1, so a reloaded conversation showed "Confidence 8500" in
    // the trace and would misread a persisted Medium (65) as High (65 >= 0.85).
    // Normalize any >1 value back to the 0..1 scale on load.
    confidenceScore:
      typeof meta.confidenceScore === 'number'
        ? (meta.confidenceScore as number) > 1
          ? (meta.confidenceScore as number) / 100
          : (meta.confidenceScore as number)
        : undefined,
    confidenceRationale:
      typeof meta.confidenceRationale === 'string'
        ? (meta.confidenceRationale as string)
        : undefined,
    timestamp: new Date(m.createdAt),
    // Review BF-11 — a truth-critical banner ("No record was created…") was
    // stream-only; on reload the fabricated answer rendered with no notice.
    qualityBanner: bannerFromMetadata(meta),
    metadata: {
      model: typeof meta.model === 'string' ? meta.model : undefined,
      totalTokens:
        typeof meta.totalTokens === 'number' ? meta.totalTokens : undefined,
      latencyMs:
        typeof meta.latencyMs === 'number' ? meta.latencyMs : undefined,
      intent: typeof meta.intent === 'string' ? meta.intent : undefined,
      ragSourceCount:
        typeof meta.ragSourceCount === 'number'
          ? meta.ragSourceCount
          : undefined,
    },
  };
}

export const Route = createFileRoute('/ask-argus/')({
  component: AskArgusPage,
});

function AskArgusPage() {
  const queryClient = useQueryClient();
  const { highlights, clear: clearHighlights } = useAttachedHighlights();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [historyLoadError, setHistoryLoadError] = useState<string | null>(null);
  const [rightOpen, setRightOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Pipeline trace shared across the turn.
  const pipelineRef = useRef<PipelineStage[] | undefined>(undefined);

  // Fetch attached tools for the active conversation so @mention autocompletes
  // surface what's pinned to THIS chat.
  const [attachedTools, setAttachedTools] = useState<
    import('@/components/ask-argus/use-mention-candidates').AttachedToolItem[] | null
  >(null);
  useEffect(() => {
    if (!activeConversationId || !UUID_RE.test(activeConversationId)) {
      setAttachedTools(null);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ attached: import('@/components/ask-argus/use-mention-candidates').AttachedToolItem[] }>(
        `/conversations/${activeConversationId}/attached-tools`,
      )
      .then((res) => {
        if (!cancelled) setAttachedTools(res.attached);
      })
      .catch(() => {
        if (!cancelled) setAttachedTools(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeConversationId]);

  // ── Live SSE chat stream ────────────────────────────────────────────────
  const chatStream = useChatStream({
    endpoint: '/ecosystem/query',
    authToken: () => {
      try {
        return localStorage.getItem('accessToken');
      } catch {
        return null;
      }
    },
    onAgentBubble: ({ bubbleId, bubbleKey, agentId, displayName, kind, retryPrompt }) => {
      setMessages((prev) => [
        ...prev,
        {
          id: bubbleId,
          role: 'assistant',
          content: '',
          agentId: bubbleKey === '__default__' ? undefined : agentId,
          agentDisplayName: displayName,
          agentKind: kind,
          timestamp: new Date(),
          retryPrompt,
        },
      ]);
    },
    onMetadata: ({ bubbleId, model, intent, displayName, kind }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                agentDisplayName: displayName ?? m.agentDisplayName,
                agentKind: kind ?? m.agentKind,
                metadata: {
                  ...(m.metadata ?? {}),
                  model: model ?? m.metadata?.model,
                  intent: intent ?? m.metadata?.intent,
                },
              }
            : m,
        ),
      );
    },
    onToken: ({ bubbleId, fullContent }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId ? { ...m, content: fullContent } : m,
        ),
      );
    },
    onCitation: ({ bubbleId, citations }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? { ...m, citations: citations.length > 0 ? citations : undefined }
            : m,
        ),
      );
    },
    onPipeline: ({ bubbleId, stages }) => {
      pipelineRef.current = stages as PipelineStage[];
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? { ...m, pipeline: stages as PipelineStage[] }
            : m,
        ),
      );
    },
    onConfidence: ({ bubbleId, confidence, rationale }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                confidenceScore: confidence,
                confidenceRationale: rationale,
              }
            : m,
        ),
      );
    },
    onBanner: ({ bubbleId, level, kind, message }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId ? { ...m, qualityBanner: { level, kind, message } } : m,
        ),
      );
    },
    onFollowUp: ({ bubbleId, suggestions }) => {
      setMessages((prev) => {
        // The follow_up event arrives after `done` with the default bubbleId;
        // if that no longer matches, attach to the latest assistant message.
        if (prev.some((m) => m.id === bubbleId)) {
          return prev.map((m) => (m.id === bubbleId ? { ...m, followUps: suggestions } : m));
        }
        const lastIdx = [...prev].reverse().findIndex((m) => m.role === 'assistant');
        if (lastIdx === -1) return prev;
        const idx = prev.length - 1 - lastIdx;
        return prev.map((m, i) => (i === idx ? { ...m, followUps: suggestions } : m));
      });
    },
    onError: ({ bubbleId, error, timedOut }) => {
      if (timedOut) {
        toast.error('Response timed out — server stopped sending updates');
      } else if (error.code?.startsWith('HTTP_')) {
        toast.error(`Ask Argus error (${error.code.replace('HTTP_', '')})`);
      } else if (error.code === 'SSE_RECONNECT_FAILED') {
        toast.error('Lost connection to Ask Argus');
      } else {
        toast.error(error.message);
      }
      setMessages((prev) =>
        prev.map((m) => (m.id === bubbleId ? { ...m, error } : m)),
      );
    },
    onDone: ({ bubbleId, totalTokens, latencyMs, messageId }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                metadata: {
                  ...(m.metadata ?? {}),
                  totalTokens: totalTokens ?? m.metadata?.totalTokens,
                  latencyMs: latencyMs ?? m.metadata?.latencyMs,
                  // Keep the bubble id as the React key (follow-ups arrive
                  // after `done` and still address it) and carry the real
                  // persisted id alongside, so rating works on this turn.
                  ...(messageId ? { serverMessageId: messageId } : {}),
                },
              }
            : m,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onAbort: ({ bubbleId }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== bubbleId) return m;
          const existing = m.content;
          return {
            ...m,
            content:
              existing + (existing ? '\n\n' : '') + '_Generation stopped._',
          };
        }),
      );
    },
  });

  const {
    isStreaming,
    phase: chatStreamPhase,
    activePipelinePhaseLabel,
    activeClassification,
    reconnectState,
    send: chatSend,
    abort: chatAbort,
  } = chatStream;

  // Issue #1323 — map the live useChatStream phase into the
  // ChatMessages visual phase so the ArgusEye state machine
  // tracks real stream lifecycle (awaiting / tool_calling / streaming
  // / error), not just a single Thinking placeholder. Codex amend on
  // PR #1328 required wiring this through the route. Mapping is
  // owned by chatStreamPhaseToMessagesPhase in chat-messages.tsx and
  // pinned by chat-messages-indicator.test.ts.
  const streamingPhase = chatStreamPhaseToMessagesPhase(chatStreamPhase);

  // Scope context panel to ONLY the most-recent assistant message's
  // citations (founder fix).
  const latestAssistant = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i];
    }
    return undefined;
  }, [messages]);

  const currentResponseCitations = useMemo<Citation[]>(
    () => latestAssistant?.citations ?? [],
    [latestAssistant],
  );

  // Auto-open the right panel as soon as citations arrive.
  const citationCount = currentResponseCitations.length;
  useEffect(() => {
    if (citationCount > 0) {
      setRightOpen(true);
    }
  }, [citationCount]);

  const handleSelectConversation = useCallback(async (id: string) => {
    setActiveConversationId(id);
    setHistoryLoadError(null);
    if (UUID_RE.test(id)) {
      try {
        const res = await apiClient.get<{ data: MessageDTO[] }>(
          `/conversations/${id}/messages`,
        );
        setMessages(res.data.map(dtoToMessage));
      } catch (err) {
        setMessages([]);
        const reason =
          err instanceof Error ? err.message : 'unknown error';
        setHistoryLoadError(reason);
        toast.error(`Could not load conversation history (${reason})`);
      }
    } else {
      setMessages([]);
    }
  }, []);

  const handleRetryHistoryLoad = useCallback(() => {
    if (activeConversationId) {
      void handleSelectConversation(activeConversationId);
    }
  }, [activeConversationId, handleSelectConversation]);

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setHistoryLoadError(null);
    setRightOpen(false);
  }, []);

  const handleStopGeneration = useCallback(() => {
    chatAbort();
  }, [chatAbort]);

  // Issue #1323 / Codex amend — route-level in-flight latch.
  //
  // ChatInput already has a same-frame latch (`submitInFlightRef`) to
  // collapse double-fires from the composer. But the route exposes
  // `handleSubmit` to MULTIPLE other call-sites that bypass the
  // composer entirely: starter-prompt clicks, followup chips, retry
  // buttons, regenerate, continue, and edit-resubmit. Each of those
  // calls `handleSubmit(text)` directly. A user double-clicking a
  // retry button, or pressing a starter prompt while a previous one
  // is still creating the conversation, could fire two POSTs before
  // the `isStreaming` flag flips.
  //
  // The latch closes the window between handleSubmit entry and the
  // point where chatSend (or the conversation-create failure path)
  // has been reached. Once we cross that point, `isStreaming` becomes
  // the authoritative guard and the latch is released.
  const submitInFlightRef = useRef(false);

  /**
   * The composer's live selection (agent, model, thinking mode, toggles),
   * republished by ChatInput on every change. Used as the fallback for turns
   * that don't originate in the composer so those sends honour what the toolbar
   * is actually displaying rather than reverting to DEFAULT_TOGGLES.
   */
  const composerContextRef = useRef<ComposerChatSubmitContext | null>(null);
  // Mirror of `messages` for callbacks that must read the latest list without
  // taking it as a dependency. handleRate runs mid-conversation and depending
  // on `messages` directly would re-create it on every streamed token.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const handleComposerContextChange = useCallback(
    (next: ComposerChatSubmitContext) => {
      composerContextRef.current = next;
    },
    [],
  );

  const handleSubmit = useCallback(
    async (text: string, ctx?: ComposerChatSubmitContext) => {
      if (isStreaming) return;
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;

      try {
        const body = formatHighlightsForSubmit(highlights, text);
        if (highlights.length > 0) clearHighlights();

        // A turn can start from six places that are not the composer: a
        // follow-up chip, Regenerate, Retry, Edit-resend, Continue, and a
        // starter prompt. None of them can supply a submit context, so this
        // used to fall back to DEFAULT_TOGGLES and drop agent/model entirely —
        // a user who selected "Library Only" (answers restricted to governed
        // documents) and then clicked a follow-up silently got "Library + AI",
        // with the toolbar still showing their original choice. The composer now
        // publishes its live state, so fall back to THAT and only then to
        // defaults.
        const effectiveCtx = ctx ?? composerContextRef.current ?? undefined;
        const toggles = serializeTogglesForRequest(
          effectiveCtx?.toggles ?? DEFAULT_TOGGLES,
        );

        const userMsg: Message = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: body,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMsg]);

        let convId =
          activeConversationId && UUID_RE.test(activeConversationId)
            ? activeConversationId
            : null;
        if (!convId) {
          try {
            const created = await apiClient.post<ConversationDTO>(
              '/conversations',
              { title: (text || body).slice(0, 80) },
            );
            convId = created.id;
            setActiveConversationId(created.id);
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
          } catch {
            toast.error('Could not create conversation');
            return;
          }
        }

        pipelineRef.current = undefined;

        await chatSend(body, {
          conversationId: convId,
          agentId: effectiveCtx?.agent || undefined,
          brand: effectiveCtx?.model || undefined,
          toggles,
        });
      } finally {
        // Release the latch once the request has either been handed
        // off to chatSend (after which isStreaming is the source of
        // truth) OR failed at conversation-create. A subsequent
        // user-initiated submit is allowed; the isStreaming check at
        // the top of this function still rejects concurrent submits
        // while the stream is in flight.
        submitInFlightRef.current = false;
      }
    },
    [
      activeConversationId,
      isStreaming,
      queryClient,
      highlights,
      clearHighlights,
      chatSend,
    ],
  );

  const handleStarterPrompt = useCallback(
    (prompt: string) => {
      void handleSubmit(prompt);
    },
    [handleSubmit],
  );

  const handleFollowUp = useCallback(
    (text: string) => {
      void handleSubmit(text);
    },
    [handleSubmit],
  );

  const handleRetry = useCallback(
    (prompt: string) => {
      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].error) {
            next.splice(i, 1);
            break;
          }
        }
        if (next.length > 0 && next[next.length - 1].role === 'user') {
          next.pop();
        }
        return next;
      });
      void handleSubmit(prompt);
    },
    [handleSubmit],
  );

  const handleRate = useCallback(
    async (
      messageId: string,
      rating: 'positive' | 'negative',
      reason?: string,
    ) => {
      if (!activeConversationId || !UUID_RE.test(activeConversationId)) {
        toast.error('Cannot record feedback on an unsaved conversation');
        return;
      }
      // The streamed bubble carries a synthetic local id ("asst-<ts>-<n>"),
      // which the feedback endpoint - keyed by a real message UUID - rejects.
      // The server now reports the persisted row id on `done`, so prefer that:
      // without it, rating was permanently unavailable on the turn the user had
      // just read, which is exactly when people want to rate an answer.
      const local = messagesRef.current.find((m) => m.id === messageId);
      const serverId = local?.metadata?.serverMessageId;
      const targetId =
        typeof serverId === 'string' && UUID_RE.test(serverId) ? serverId : messageId;
      if (!UUID_RE.test(targetId)) {
        toast.error('Feedback will be enabled after the response saves');
        return;
      }
      const feedback = { rating, ...(reason ? { reason } : {}) };
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                feedback: feedback as {
                  rating: 'positive' | 'negative';
                  reason?: string;
                },
              }
            : m,
        ),
      );
      try {
        await apiClient.post(
          `/conversations/${activeConversationId}/messages/${targetId}/feedback`,
          { rating, ...(reason ? { comment: reason } : {}) },
        );
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, feedback: undefined } : m,
          ),
        );
        toast.error('Could not save feedback');
        // eslint-disable-next-line no-console
        console.error('feedback persist failed', err);
      }
    },
    [activeConversationId],
  );

  const handleRegenerate = useCallback(
    (messageId: string) => {
      if (isStreaming) return;
      let prompt: string | null = null;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx < 0) return prev;
        for (let i = idx - 1; i >= 0; i--) {
          if (prev[i].role === 'user') {
            prompt = prev[i].content;
            return prev.slice(0, i);
          }
        }
        return prev;
      });
      if (prompt) {
        void handleSubmit(prompt);
      } else {
        toast.error('Could not find the original prompt to regenerate');
      }
    },
    [handleSubmit, isStreaming],
  );

  const handleContinue = useCallback(
    (_messageId: string) => {
      if (isStreaming) return;
      void handleSubmit('Please continue.');
    },
    [handleSubmit, isStreaming],
  );

  const handleEdit = useCallback(
    (messageId: string, newContent: string) => {
      if (isStreaming) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx < 0) return prev;
        return prev.slice(0, idx);
      });
      void handleSubmit(newContent);
    },
    [handleSubmit, isStreaming],
  );

  const handleShareConversation = useCallback(() => {
    if (!activeConversationId || !UUID_RE.test(activeConversationId)) {
      toast.error('Save the conversation first before sharing');
      return;
    }
    setShareDialogOpen(true);
  }, [activeConversationId]);

  const hasMessages = messages.length > 0;
  const hasFinishedResponse = !!latestAssistant && !isStreaming;
  const hasShareableConversation = !!(
    activeConversationId && UUID_RE.test(activeConversationId)
  );

  // Focus-return to composer on streaming end (a11y).
  const composerRef = useRef<HTMLDivElement>(null);
  const prevStreamingRef = useRef(isStreaming);
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = isStreaming;
    if (wasStreaming && !isStreaming && composerRef.current) {
      const textarea = composerRef.current.querySelector<HTMLTextAreaElement>('textarea');
      textarea?.focus();
    }
  }, [isStreaming]);

  return (
    <CitationVisibilityProvider>
      <ChatAnnouncerProvider>
        {/* Demo-blessed layout: plain flex, fixed-width side panels, no Resizable */}
        <div
          className="flex h-full overflow-hidden"
          data-testid="ask-argus-layout"
        >
          {/* ── Left: Conversation Sidebar — ALWAYS visible ─────────────── */}
          <div
            className="w-[280px] shrink-0 border-r border-border/40 overflow-hidden"
            data-testid="ask-argus-left-panel"
          >
            <ConversationSidebar
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onNewChat={handleNewChat}
            />
          </div>

          {/* ── Center: Chat ───────────────────────────────────────────── */}
          <div
            className="flex-1 flex flex-col min-w-0 overflow-hidden"
            data-testid="ask-argus-center-panel"
          >
            {/* Tiny degradation indicators (only render when unhealthy) */}
            <OfflineBanner />
            <ReconnectBadge reconnectState={reconnectState} />

            {/* Tools strip + Share + panel toggle — single row */}
            <ChatToolsStrip
              conversationId={
                activeConversationId && UUID_RE.test(activeConversationId)
                  ? activeConversationId
                  : null
              }
              rightAction={
                <div className="flex items-center gap-1">
                  {hasShareableConversation && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px] text-muted-foreground gap-1 shrink-0"
                      onClick={handleShareConversation}
                      aria-label="Share conversation"
                      title="Share conversation"
                      data-testid="share-conversation-button"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      <span>Share</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-muted-foreground gap-1 shrink-0"
                    onClick={() => setRightOpen(!rightOpen)}
                    aria-label={rightOpen ? 'Hide context panel' : 'Show context panel'}
                    title={rightOpen ? 'Hide context panel' : 'Show context panel'}
                  >
                    {rightOpen ? (
                      <PanelRightClose className="h-3.5 w-3.5" />
                    ) : (
                      <PanelRight className="h-3.5 w-3.5" />
                    )}
                    <span>{rightOpen ? 'Hide' : 'Panel'}</span>
                  </Button>
                </div>
              }
            />

            {/* Optional banners — render nothing when payload is empty */}
            <ClassificationBanner classification={activeClassification} />

            {/* Chat content */}
            {historyLoadError ? (
              <>
                <div className="flex-1 flex items-center justify-center p-6">
                  <div
                    role="alert"
                    data-testid="ask-argus-history-load-error"
                    className="max-w-md w-full rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
                  >
                    <p className="text-sm font-medium text-destructive">
                      Could not load conversation history
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 break-words">
                      {historyLoadError}
                    </p>
                    <div className="mt-3">
                      <button
                        type="button"
                        className="inline-flex items-center h-7 px-3 text-xs rounded-md border border-border bg-background hover:bg-muted transition-colors gap-1"
                        onClick={handleRetryHistoryLoad}
                        data-testid="ask-argus-history-load-retry"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
                <AttachedHighlightsChips />
                <div ref={composerRef} data-testid="ask-argus-composer">
                  <ChatInput
                    onSubmit={handleSubmit}
                    disabled={isStreaming}
                    attachedTools={attachedTools}
                    isStreaming={isStreaming}
                    onStop={handleStopGeneration}
                    onContextChange={handleComposerContextChange}
                  />
                </div>
              </>
            ) : hasMessages ? (
              <>
                <ChatMessages
                  messages={messages}
                  isStreaming={isStreaming}
                  streamingPhase={streamingPhase}
                  streamingLabel={streamingPhase === 'thinking' ? activePipelinePhaseLabel : undefined}
                  onRetry={handleRetry}
                  onRate={handleRate}
                  onRegenerate={handleRegenerate}
                  onContinue={handleContinue}
                  onEdit={handleEdit}
                  onFollowUp={handleFollowUp}
                />
                <AttachedHighlightsChips />
                <div ref={composerRef} data-testid="ask-argus-composer">
                  <ChatInput
                    onSubmit={handleSubmit}
                    disabled={isStreaming}
                    attachedTools={attachedTools}
                    isStreaming={isStreaming}
                    onStop={handleStopGeneration}
                    onContextChange={handleComposerContextChange}
                  />
                </div>
              </>
            ) : (
              <>
                <AskArgusWelcome onSelectPrompt={handleStarterPrompt} />
                <AttachedHighlightsChips />
                <div ref={composerRef} data-testid="ask-argus-composer">
                  <ChatInput
                    onSubmit={handleSubmit}
                    placeholder="Ask Argus anything..."
                    disabled={isStreaming}
                    attachedTools={attachedTools}
                    isStreaming={isStreaming}
                    onStop={handleStopGeneration}
                    onContextChange={handleComposerContextChange}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── Right: Context Panel ───────────────────────────────────── */}
          <div
            className={cn(
              'shrink-0 border-l border-border/40 overflow-hidden transition-all duration-200',
              rightOpen ? 'w-[300px]' : 'w-0',
            )}
            data-testid="ask-argus-right-panel"
            data-open={rightOpen ? 'true' : 'false'}
          >
            {rightOpen && (
              <ContextPanel
                citations={currentResponseCitations}
                isStreaming={isStreaming}
                hasResponse={hasFinishedResponse}
              />
            )}
          </div>
        </div>

        {/* Share dialog — mounted at route level */}
        {activeConversationId && UUID_RE.test(activeConversationId) && (
          <ShareLinkDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            conversationId={activeConversationId}
          />
        )}
      </ChatAnnouncerProvider>
    </CitationVisibilityProvider>
  );
}
